import React from 'react';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { ProjectRequirement, MoscowPriority, MoscowPriorityLabels } from '../../../models/ProjectRequirement';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { useTranslation } from 'react-i18next';

const ProjectRequirements: React.FC<{ scenarioId: string; setScenarioId: (id: string) => void; onDirtyChange?: (dirty: boolean) => void }> = ({ scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId);
  const [saving, setSaving] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const provider = React.useMemo(() => new ProjectsScenariosRestDataProvider(), []);

  const columns: DetailedListColumn<ProjectRequirement>[] = React.useMemo(() => [
    { key: 'name', header: t('common:name'), required: true },
    { key: 'description', header: t('common:description'), type: 'multiline', multiline: true, rows: 3 },
    {
      key: 'moscowPriority',
      header: 'Priority',
      type: 'select',
      required: true,
      options: Object.values(MoscowPriority).map(value => ({ label: MoscowPriorityLabels[value], value })),
      render: (item: ProjectRequirement) => item.moscowPriority ? MoscowPriorityLabels[item.moscowPriority] : '-'
    },
  ], [t]);

  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);
  if (!scenario) return <div>{t('screens:projects.noScenarioFound')}</div>;

  const handleChange = (items: ProjectRequirement[]) => {
    // Ensure every requirement has a GUID
    const itemsWithIds = items.map(item => ({ ...item, id: item.id || generateGuid() }));
    setScenarios(scenarios.map(s => s.id === scenarioId ? { ...s, requirements: itemsWithIds } : s));
    setIsDirty(true);
  };

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return;
    setSaving(true);
    try {
      await provider.update(scenario.id, { ...scenario, requirements: scenario.requirements });
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  function generateGuid() {
    // RFC4122 version 4 compliant UUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  return (
    <>
      <ScenarioSwitcher scenarioId={scenarioId} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />
      <SubsectionPanel title={t('screens:projectRequirements.title')}>
        <DetailedListComponent
          columns={columns}
          items={scenario.requirements || []}
          rowKey={r => r.id}
          onChange={items => handleChange(items as ProjectRequirement[])}
          editRole="Manager,Admin"
          addRole="Manager,Admin"
          deleteRole="Manager,Admin"
        />
        {saving && <div style={{ marginTop: 12 }}>{t('common:saving')}</div>}
      </SubsectionPanel>
    </>
  );
};

export default ProjectRequirements;
